"use client";

import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import StatusBadge from "@/components/dashboard/StatusBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { CreditCard, TrendingUp, ArrowUpRight, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getBilling } from "@/services/api";
import type { BillingInfo } from "@/services/api";

function formatStorageTb(bytes: number): string {
  const tb = bytes / 1024 ** 4;
  return tb >= 1 ? tb.toFixed(1) : (bytes / 1024 ** 3).toFixed(2);
}

function formatDate(isoDate: string): string {
  if (!isoDate) return "—";
  const d = new Date(isoDate + "T12:00:00Z");
  return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function formatAmount(amount: number): string {
  return `€${amount.toFixed(2)}`;
}

const paymentHistory = [
  { date: "2025-02-01", amount: "€890.00", plan: "Starter", status: "success" as const },
  { date: "2025-01-01", amount: "€890.00", plan: "Starter", status: "success" as const },
];

const Billing = () => {
  const { t } = useTranslation();
  const [billing, setBilling] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getBilling()
      .then(setBilling)
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const storageUsedTb = billing ? formatStorageTb(billing.storageUsedBytes) : "—";
  const storageLimitTb = billing != null ? String(billing.storageLimitTb) : "—";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-foreground">{String(t("billing.title"))}</h1>
        <p className="text-sm text-muted-foreground mt-1">{String(t("billing.subtitle"))}</p>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {loading && !billing && (
        <div className="text-sm text-muted-foreground">Loading…</div>
      )}

      <div className="rounded-lg border border-border/50 bg-card p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-md bg-primary/10">
                <CreditCard className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {billing ? billing.planName : (loading ? "…" : String(t("billing.enterprisePlan")))}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {billing ? formatAmount(billing.monthlyPrice) : (loading ? "…" : "—")} {String(t("billing.perMonth"))}
                </p>
              </div>
            </div>
            <div className="space-y-2 max-w-xs">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">{String(t("billing.storageUsage"))}</span>
                <span className="font-medium">
                  {billing != null ? `${billing.storageUsagePercent}%` : (loading ? "…" : "—")}
                </span>
              </div>
              <Progress value={billing?.storageUsagePercent ?? 0} className="h-2" />
              <p className="text-xs text-muted-foreground">
                {storageUsedTb} TB / {storageLimitTb} TB
              </p>
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
              <span className="text-muted-foreground">{String(t("billing.overageCost"))}</span>
              <span>{billing ? formatAmount(billing.overageCost) : (loading ? "…" : "—")}</span>
              <span className="text-muted-foreground">{String(t("billing.nextInvoice"))}</span>
              <span>{billing ? formatDate(billing.nextInvoiceDate) : (loading ? "…" : "—")}</span>
              <span className="text-muted-foreground">{String(t("billing.nextAmount"))}</span>
              <span className="font-medium">
                {billing ? formatAmount(billing.nextAmount) : (loading ? "…" : "—")}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-border/50 bg-card p-5 shadow-sm">
        <h3 className="text-sm font-medium mb-4">{String(t("billing.planOptions"))}</h3>
        <div className="flex flex-wrap gap-3">
          <Button size="sm" className="gap-1.5">
            <ArrowUpRight className="h-3.5 w-3.5" /> {String(t("billing.upgradeEnterprise"))}
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5">
            <TrendingUp className="h-3.5 w-3.5" /> {String(t("billing.switchAnnual"))}
          </Button>
          <Button variant="outline" size="sm">{String(t("billing.addStorage"))}</Button>
        </div>
      </div>

      <div className="rounded-lg border border-border/50 bg-card shadow-sm">
        <div className="p-5 border-b border-border/50">
          <h3 className="text-sm font-medium">{String(t("billing.paymentHistory"))}</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs">{String(t("billing.date"))}</TableHead>
              <TableHead className="text-xs">{String(t("billing.amount"))}</TableHead>
              <TableHead className="text-xs">{String(t("billing.plan"))}</TableHead>
              <TableHead className="text-xs">{String(t("billing.status"))}</TableHead>
              <TableHead className="text-xs w-[1%]">{}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paymentHistory.map((p, i) => (
              <TableRow key={i}>
                <TableCell className="text-sm">{p.date}</TableCell>
                <TableCell className="text-sm font-medium">{p.amount}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{p.plan}</TableCell>
                <TableCell><StatusBadge status={p.status} label={p.status === "success" ? String(t("billing.paid")) : String(t("billing.retry"))} /></TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 text-xs"
                    onClick={() => {}}
                  >
                    <Download className="h-3.5 w-3.5" />
                    {String(t("billing.downloadInvoice"))}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default Billing;
